import { useEffect, useState } from "react";
import axios from "axios";
import { Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { TOURS, IMAGES } from "@/tours";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Enquiry({ preselectedTour, onConsumePreselect }) {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", tour: "", guests: "", date: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselectedTour) {
      setForm((f) => ({ ...f, tour: preselectedTour }));
      onConsumePreselect?.();
    }
  }, [preselectedTour, onConsumePreselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), lang });
      setSent(true);
      toast.success(t.enquiry.successTitle);
    } catch (err) {
      console.error("Enquiry submit failed:", err?.response?.status, err?.message);
      toast.error(t.enquiry.error);
      const subject = encodeURIComponent(`${t.enquiry.errorFallbackSubject}: ${form.tour}`);
      const body = encodeURIComponent(
        `${t.enquiry.name}: ${form.name}\nEmail: ${form.email}\nTour: ${form.tour}\n${t.enquiry.guests}: ${form.guests || "-"}\n${t.enquiry.date}: ${form.date || "-"}\n\n${form.message}`
      );
      window.location.href = `mailto:${t.footer.email}?subject=${subject}&body=${body}`;
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="richiedi" data-testid="enquiry-section" className="relative scroll-mt-24 py-24 lg:py-36 overflow-hidden">
      <img src={IMAGES.corricella} alt="Le case colorate di Marina Corricella, Procida" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-azure/90" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-transparent to-ink/50" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-dune mb-5">{t.enquiry.overline}</p>
          </Reveal>
          <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7]">
            <MaskedLine delay={0.1}>{t.enquiry.title1}</MaskedLine>
            <MaskedLine delay={0.22}>
              <span className="italic text-dune">{t.enquiry.title2}</span>
            </MaskedLine>
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-7 text-base lg:text-lg leading-relaxed text-[#F3EFE9]/85 max-w-md">{t.enquiry.sub}</p>
            <p className="mt-8 flex items-start gap-3 text-sm text-[#F3EFE9]/65 max-w-sm">
              <ShieldCheck className="w-5 h-5 text-dune shrink-0 mt-0.5" strokeWidth={1.75} />
              {t.enquiry.privacy}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7 bg-[#FDFBF7] rounded-sm shadow-2xl p-8 lg:p-12">
          {sent ? (
            <div data-testid="enquiry-success" className="h-full min-h-[420px] flex flex-col items-start justify-center bg-sand rounded-sm p-10 lg:p-14">
              <span className="w-14 h-14 rounded-full bg-olive/15 text-olive flex items-center justify-center mb-6">
                <Send className="w-6 h-6" />
              </span>
              <h3 className="font-serif text-3xl lg:text-4xl font-medium tracking-tight text-ink mb-4">{t.enquiry.successTitle}</h3>
              <p className="text-base lg:text-lg leading-relaxed text-ink/70 max-w-md">{t.enquiry.success}</p>
            </div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="enquiry-name" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.enquiry.name}</label>
                  <Input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder={t.enquiry.namePh}
                    className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base focus-visible:ring-0 focus-visible:border-[#C9A227] placeholder:text-sage/60" />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.enquiry.email}</label>
                  <Input id="enquiry-email" data-testid="enquiry-email-input" required type="email" inputMode="email" autoCapitalize="none" autoComplete="email" autoCorrect="off" value={form.email} onChange={set("email")} placeholder={t.enquiry.emailPh}
                    className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base focus-visible:ring-0 focus-visible:border-[#C9A227] placeholder:text-sage/60" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.enquiry.tour}</label>
                <Select required value={form.tour} onValueChange={(v) => setForm((f) => ({ ...f, tour: v }))}>
                  <SelectTrigger data-testid="enquiry-tour-select" className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base focus:ring-0 data-[placeholder]:text-sage/60">
                    <SelectValue placeholder={t.enquiry.tourPh} />
                  </SelectTrigger>
                  <SelectContent data-testid="enquiry-tour-options" className="bg-[#FDFBF7]">
                    {TOURS.map((tour) => (
                      <SelectItem key={tour.slug} value={tour[lang].title} data-testid={`enquiry-tour-option-${tour.slug}`}>
                        {tour[lang].title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="enquiry-guests" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.enquiry.guests}</label>
                  <Input id="enquiry-guests" data-testid="enquiry-guests-input" value={form.guests} onChange={set("guests")} placeholder={t.enquiry.guestsPh}
                    className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base focus-visible:ring-0 focus-visible:border-[#C9A227] placeholder:text-sage/60" />
                </div>
                <div>
                  <label htmlFor="enquiry-date" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.enquiry.date}</label>
                  <Input id="enquiry-date" data-testid="enquiry-date-input" type="date" value={form.date} onChange={set("date")}
                    className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base focus-visible:ring-0 focus-visible:border-[#C9A227] text-ink/80" />
                </div>
              </div>

              <div>
                <label htmlFor="enquiry-message" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.enquiry.message}</label>
                <Textarea id="enquiry-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} placeholder={t.enquiry.messagePh}
                  className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base focus-visible:ring-0 focus-visible:border-[#C9A227] placeholder:text-sage/60 resize-none" />
              </div>

              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={sending}
                className="group inline-flex items-center gap-3 rounded-full bg-[#C9A227] px-10 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#0F2C50] hover:bg-[#E2B34E] transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? t.enquiry.sending : t.enquiry.submit}
                <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
